import {
  mapAssetDownloadUrlDto,
  mapAssetListResponseDto,
  mapCanvasPoolAssetDto,
  mapCanvasPoolListResponseDto,
  mapCanvasPoolRegisterResultDto,
  type AssetDownloadUrl,
  type AssetDownloadUrlDto,
  type AssetListResponse,
  type AssetListResponseDto,
  type CanvasPoolAsset,
  type CanvasPoolAssetDto,
  type CanvasPoolListResponse,
  type CanvasPoolListResponseDto,
  type CanvasPoolRegisterResultDto,
} from '@/types';

import type { AssetsService, ListAssetsOptions, ListPoolAssetsOptions } from '../assetsService';

import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`AssetsService`(對齊 docs/22;R372 起 + 畫布素材池 docs/127)
 * -----------------------------------------------------------------------------
 * - ``GET /api/projects/{pid}/assets`` — 生成產物 × job context(`jobs/{jid}/output_assets`)
 * - ``GET|PATCH /api/projects/{pid}/project_assets`` + ``POST …/assets/from_upload``
 *   — 畫布素材池(ProjectAsset 集合;**與上面是兩個不同集合**,見 assetsService.ts)
 *
 * **Auth**:cookie + refresh chain(同既有)
 * **Pagination**:cursor-based(opaque base64 string from BE,client 不解析)
 */

const PATH = {
  list: (pid: string) => `/api/projects/${encodeURIComponent(pid)}/assets`,
  downloadUrl: (pid: string, assetId: string) =>
    `/api/projects/${encodeURIComponent(pid)}/assets/${encodeURIComponent(assetId)}/download-url`,
  registerFromUpload: (pid: string) =>
    `/api/projects/${encodeURIComponent(pid)}/assets/from_upload`,
  poolList: (pid: string) => `/api/projects/${encodeURIComponent(pid)}/project_assets`,
  poolPatch: (pid: string, assetId: string) =>
    `/api/projects/${encodeURIComponent(pid)}/project_assets/${encodeURIComponent(assetId)}`,
} as const;

/** 池「一次翻完」的頁數硬上限(100/頁 × 40 頁;防雲端資料異常時無限迴圈,同 BE 讀端慣例)。 */
const POOL_LIST_MAX_PAGES = 40;

function buildQueryString(opts?: ListAssetsOptions): string {
  if (!opts) return '';
  const params = new URLSearchParams();
  if (opts.nodeId !== undefined) params.set('node_id', opts.nodeId);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.cursor !== undefined) params.set('cursor', opts.cursor);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

function buildPoolQueryString(opts?: ListPoolAssetsOptions): string {
  if (!opts) return '';
  const params = new URLSearchParams();
  if (opts.source !== undefined) params.set('source', opts.source);
  if (opts.assetKind !== undefined) params.set('asset_kind', opts.assetKind);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.cursor !== undefined) params.set('cursor', opts.cursor);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function createHttpAssetsService(config: HttpClientConfig): AssetsService {
  const listPoolPage = async (
    projectId: string,
    opts?: ListPoolAssetsOptions,
  ): Promise<CanvasPoolListResponse> => {
    const env = await httpRequestWithRefresh<CanvasPoolListResponseDto>(
      config,
      'GET',
      PATH.poolList(projectId) + buildPoolQueryString(opts),
    );
    return mapCanvasPoolListResponseDto(unwrapResult(env, 'assets.listPoolPage'));
  };

  return {
    async list(projectId, opts): Promise<AssetListResponse> {
      const env = await httpRequestWithRefresh<AssetListResponseDto>(
        config,
        'GET',
        PATH.list(projectId) + buildQueryString(opts),
      );
      return mapAssetListResponseDto(unwrapResult(env, 'assets.list'));
    },

    async getDownloadUrl(projectId, assetId): Promise<AssetDownloadUrl> {
      const env = await httpRequestWithRefresh<AssetDownloadUrlDto>(
        config,
        'GET',
        PATH.downloadUrl(projectId, assetId),
      );
      return mapAssetDownloadUrlDto(unwrapResult(env, 'assets.getDownloadUrl'));
    },

    async registerUploadToPool(projectId, uploadId, metadata) {
      const env = await httpRequestWithRefresh<CanvasPoolRegisterResultDto>(
        config,
        'POST',
        PATH.registerFromUpload(projectId),
        // metadata 只在有內容時帶(空物件也合法,但不帶讓 wire 乾淨、與「上傳當下
        // 先登記身分」的語意一致)。
        metadata && Object.keys(metadata).length > 0
          ? { upload_id: uploadId, metadata }
          : { upload_id: uploadId },
      );
      return mapCanvasPoolRegisterResultDto(unwrapResult(env, 'assets.registerUploadToPool'));
    },

    listPoolPage,

    async listPoolAll(projectId, opts): Promise<CanvasPoolAsset[]> {
      const out: CanvasPoolAsset[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < POOL_LIST_MAX_PAGES; page += 1) {
        const res = await listPoolPage(projectId, { ...opts, limit: 100, cursor });
        out.push(...res.items);
        if (!res.nextCursor) break;
        cursor = res.nextCursor;
      }
      return out;
    },

    async patchPoolMetadata(projectId, assetId, metadata): Promise<CanvasPoolAsset> {
      const env = await httpRequestWithRefresh<CanvasPoolAssetDto>(
        config,
        'PATCH',
        PATH.poolPatch(projectId, assetId),
        { metadata },
      );
      return mapCanvasPoolAssetDto(unwrapResult(env, 'assets.patchPoolMetadata'));
    },
  };
}
